import React, { useState, useEffect } from 'react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { useConsultations } from '../hooks/useConsultations';
import { ConsultationCard } from '../components/consultations/ConsultationCard';
import { EmptyConsultations } from '../components/consultations/EmptyConsultations';
import { FullscreenLoader } from '../components/ui/FullscreenLoader';
import { toast } from '../components/ui/Toast';
import { ReschedulePage } from '../components/reschedule/ReschedulePage';
import { getUrlParam } from '../utils/url';

export const ConsultationsPage: React.FC<{
  /** H-5: для Персони C — перехід на ServicesPage при порожньому стані */
  onNavigateToServices?: () => void;
}> = ({ onNavigateToServices }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { upcoming, past, cancelled, pending, nieudane, isLoading } = useConsultations();
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const tab = getUrlParam('tab');
    const orderId = getUrlParam('order_id');
    if (tab === 'przeloz' && orderId) {
      setReschedulingId(orderId);
    }
  }, []);

  const handleRescheduleClick = (id: string) => {
    setReschedulingId(id);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('tab', 'przeloz');
    newUrl.searchParams.set('order_id', id);
    window.history.pushState({}, '', newUrl);
  };

  const handleBack = () => {
    setReschedulingId(null);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('tab');
    newUrl.searchParams.delete('order_id');
    window.history.pushState({}, '', newUrl);
  };

  const handleSummaryClick = (visitId: string | number) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('tab', 'visit-summary');
    newUrl.searchParams.set('visit_id', visitId.toString());
    window.history.pushState({}, '', newUrl);
    // Trigger popstate event so CabinetShell picks it up
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (isLoading) {
    return <FullscreenLoader inline />;
  }

  if (reschedulingId) {
    // H-1: знаходимо поточну консультацію щоб показати в ReschedulePage
    const currentConsultation = [...upcoming, ...past, ...cancelled, ...pending, ...nieudane].find(c => c.id === reschedulingId) || null;
    return (
      <ReschedulePage
        onBack={handleBack}
        consultationId={reschedulingId}
        consultation={currentConsultation}
      />
    );
  }

  const hasNoConsultations = upcoming.length === 0 && past.length === 0 && cancelled.length === 0 && pending.length === 0 && nieudane.length === 0;

  if (hasNoConsultations) {
    return (
      <div className="cab-page-wrap">
        <h1 className="cab-page-title">{t('greet_title', { name: user?.firstName || '' })}</h1>
        <p className="cab-welcome-sub">{t('greet_sub')}</p>

        <div className="cab-cons-container">
          <h2 className="cab-cons-box-title">{t('nav_cons')}</h2>
          <EmptyConsultations 
            personaType={user?.personaType}
            onServicesClick={onNavigateToServices}
          />
        </div>

        <style>{`
          .cab-page-wrap {
            text-align: center;
            padding-top: 0px;
          }
          .cab-page-title {
            font-family: 'Poppins', sans-serif;
            font-weight: 700;
            font-size: 24px;
            color: var(--cab-violet);
            margin-bottom: 8px;
            text-align: left;
          }
          .cab-welcome-sub {
            font-family: 'Poppins', sans-serif;
            font-weight: 500;
            font-size: 14px;
            color: var(--cab-body, #57437E);
            margin-bottom: 24px!important;
            line-height: 1.5;
            text-align: left;
          }
          .cab-cons-container {
            background: var(--cab-light, #FAF8FE);
            border-radius: 24px;
            padding: 24px 16px;
            text-align: left;
            box-shadow: 0 4px 20px rgba(54, 23, 119, 0.02);
          }
          .cab-cons-box-title {
            font-family: 'Poppins', sans-serif;
            font-weight: 700;
            font-size: 24px;
            color: var(--cab-violet);
            margin: 0 0 20px!important;
          }
          
          /* Responsive styles for Tablet & Desktop */
          @media (min-width: 1024px) {
            .cab-page-wrap {
              text-align: left;
              padding-top: 0px;
            }
            .cab-page-title {
              font-size: 28px;
            }
            .cab-welcome-sub {
              font-size: 16px;
              text-align: left;
              margin-bottom: 0!important; 
            }
            .cab-cons-container {
              padding: 24px 0 0;
            }
          }
        `}</style>
      </div>
    );
  }

  const maxVisible = 2;
  const visibleUpcoming = isExpanded ? upcoming : upcoming.slice(0, maxVisible);
  const remainingPastSlots = Math.max(0, maxVisible - visibleUpcoming.length);
  const visiblePast = isExpanded ? past : past.slice(0, remainingPastSlots);
  const remainingCancelledSlots = Math.max(0, remainingPastSlots - visiblePast.length);
  const visibleCancelled = isExpanded ? cancelled : cancelled.slice(0, remainingCancelledSlots);
  const hiddenCount = (upcoming.length + past.length + cancelled.length + pending.length + nieudane.length) - maxVisible;
  const remainingPendingSlots = Math.max(0, remainingCancelledSlots - visibleCancelled.length);
  const visiblePending = isExpanded ? pending : pending.slice(0, remainingPendingSlots);
  const remainingNieudaneSlots = Math.max(0, remainingPendingSlots - visiblePending.length);
  const visibleNieudane = isExpanded ? nieudane : nieudane.slice(0, remainingNieudaneSlots);

  return (
    <div className="cab-page-wrap">
      <h1 className="cab-page-title">{t('greet_title', { name: user?.firstName || '' })}</h1>
      <p className="cab-welcome-sub">{t('greet_sub')}</p>

      <div className="cab-cons-container">
        <h2 className="cab-cons-box-title">{t('nav_cons')}</h2>

        <div className="cab-cons-list">
          

          {visibleUpcoming.length > 0 && (
            <div className="cab-cons-section">
              <h3 className="cab-section-title">{t('cons_actual')} ({upcoming.length})</h3>
              <div className="cab-cards-grid">
                {visibleUpcoming.map(cons => (
                  <ConsultationCard 
                    key={cons.id} 
                    consultation={cons} 
                    onRescheduleClick={() => handleRescheduleClick(cons.id)}
                    onRebookClick={() => toast(t('toast_soon'))}
                  />
                ))}
              </div>
            </div>
          )}

          {visiblePast.length > 0 && (
            <div className="cab-cons-section">
              <h3 className="cab-section-title">{t('cons_done')} ({past.length})</h3>
              <div className="cab-cards-grid">
                {visiblePast.map(cons => (
                  <ConsultationCard
                    key={cons.id}
                    consultation={cons}
                    onRescheduleClick={() => handleRescheduleClick(cons.id)}
                    onRebookClick={() => toast(t('toast_soon'))}
                    onSummaryClick={
                      () => {
                        const isDiet = (cons.serviceName && (cons.serviceName.includes('Jadłospis') || cons.serviceName.includes('dietetyk') || cons.serviceName.includes('Pakiet: konsultacja'))) || cons.cardType === 'diet_order';
                        if (isDiet && (cons.wcOrderId || cons.orderId)) {
                          handleSummaryClick(`order_${cons.wcOrderId || cons.orderId}`);
                        } else if (cons.wizytaId) {
                          handleSummaryClick(cons.wizytaId);
                        } else if (cons.wcOrderId) {
                          handleSummaryClick(`order_${cons.wcOrderId}`);
                        } else if (cons.orderId) {
                          handleSummaryClick(`order_${cons.orderId}`);
                        }
                      }
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {visiblePending.length > 0 && (
            <div className="cab-cons-section">
              <h3 className="cab-section-title">{t('status_pending') || 'Oczekuje na płatność'} ({pending.length})</h3>
              <div className="cab-cards-grid">
                {visiblePending.map(cons => (
                  <ConsultationCard
                    key={cons.id}
                    consultation={cons}
                    onRebookClick={() => toast(t('toast_soon'))}
                  />
                ))}
              </div>
            </div>
          )}

          {visibleNieudane.length > 0 && (
            <div className="cab-cons-section">
              <h3 className="cab-section-title">{t('status_nieudane')} ({nieudane.length})</h3>
              <div className="cab-cards-grid">
                {visibleNieudane.map(cons => (
                  <ConsultationCard
                    key={cons.id}
                    consultation={cons}
                  />
                ))}
              </div>
            </div>
          )}

          {visibleCancelled.length > 0 && (
            <div className="cab-cons-section cab-cancelled-section">
              <h3 className="cab-section-title">{t('cons_cancelled')} ({cancelled.length})</h3>
              <div className="cab-cards-grid">
                {visibleCancelled.map(cons => (
                  <ConsultationCard 
                    key={cons.id} 
                    consultation={cons} 
                    onRescheduleClick={() => handleRescheduleClick(cons.id)}
                    onRebookClick={() => toast(t('toast_soon'))}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {hiddenCount > 0 && (
          <div className="cab-more-row">
            {!isExpanded ? (
              <button 
                onClick={() => setIsExpanded(true)}
                className="cab-more-btn"
              >
                {t('cons_show_more', { count: hiddenCount.toString() })}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            ) : (
              <button 
                onClick={() => setIsExpanded(false)}
                className="cab-more-btn"
              >
                {t('cons_collapse')}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        .cab-page-wrap {
          text-align: center;
          padding-top: 10px;
        }
        .cab-page-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 24px;
          color: var(--cab-violet);
          margin-bottom: 8px;
          text-align: left;
        }
        .cab-welcome-sub {
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 14px;
          color: var(--cab-body, #57437E);
          margin-bottom: 24px!important;
          line-height: 1.5;
          text-align: left;
        }
        .cab-cons-container {
          background: var(--cab-light, #FAF8FE);
          border-radius: 24px;
          padding: 24px 16px;
          text-align: left;
          box-shadow: 0 4px 20px rgba(54, 23, 119, 0.02);
        }
        .cab-cons-box-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 24px;
          color: var(--cab-violet);
          margin: 0 0 20px;
        }
        .cab-cons-list {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .cab-cons-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .cab-section-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 500!important;
          font-size: 16px;
          color: var(--cab-note, #786798);
          margin: 0;
        }
        .cab-cards-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .cab-more-row {
          margin-top: 24px;
        }
        .cab-more-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 12px 40px;
          border: 1px solid var(--cab-violet, #361777);
          background: transparent;
          color: var(--cab-violet, #361777);
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 16px;
          border-radius: 100px;
          cursor: pointer;
          transition: 0.2s ease-in-out;
        }
        .cab-more-btn:hover {
          background: var(--cab-purple-main, #E6D6FA);
        }
        .cab-more-btn svg {
          stroke: currentColor;
          transition: transform 0.2s;
        }
        
        /* Responsive styles for Tablet & Desktop */
        @media (min-width: 1024px) {
          .cab-page-wrap {
            text-align: left;
            padding-top: 0px;
          }
          .cab-page-title {
            font-size: 28px;
          }
          .cab-welcome-sub {
            font-size: 16px;
            margin-bottom: 0!important; 
          }
          .cab-cons-container {
            padding: 24px 0 0;
          }
          .cab-cards-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
            gap: 20px;
          }
        }
        
        @media (min-width: 1024px) {
          .cab-cons-list {
            align-items: flex-start;
            gap: 32px;
            padding: 0;
          }
          .cab-cons-section {
            flex: 1;
            width: 100%;
          }
          .cab-cards-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
